// Edit modal for an existing PBX host (agent row).
//
// Covers the mutable fields the operator can change post-provisioning:
//   - Display name + notes
//   - PBX type (dropdown; only 'freepbx' supported today, so effectively read-only)
//   - FreePBX GraphQL API credentials (client_id / client_secret / base_url)
//
// NOT here (deliberately):
//   - endpoint_url — auto-derived from the CF tunnel hostname; changing it
//     would break the tunnel binding. If you truly need to change it,
//     tear down + re-provision.
//   - CF Access Service Token creds (access_client_id / access_client_secret) —
//     set by provisioning; rotate via a separate flow if we build one.
//     Not exposed here to keep this modal focused on the operator's
//     day-to-day config surface.
//
// The client_secret field follows the same conventions as
// FreePbxCredsModal did before this: blank on save preserves the stored
// value; explicit "Clear secret" button wipes it.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import PbxTypeSelect, { PBX_TYPES } from './PbxTypeSelect.jsx';

const DEFAULT_BASE_URL = 'http://localhost/admin/api/api/';

function agentDisplayIdForQuery(agent) {
  return agent?.id || agent?.agent_id || null;
}

export default function PbxHostEditModal({ agent, onCancel, onSave, isPending, error }) {
  const [name, setName]                 = useState(agent?.name || agent?.label || '');
  const [description, setDescription]   = useState(agent?.description || '');
  const [pbxType, setPbxType]           = useState(agent?.pbx_type || 'freepbx');
  const [clientId, setClientId]         = useState(agent?.freepbx_client_id || '');
  const [clientSecret, setClientSecret] = useState('');
  const [baseUrl, setBaseUrl]           = useState(agent?.freepbx_base_url || DEFAULT_BASE_URL);
  const [showSecret, setShowSecret]     = useState(false);
  const [gdmsSipServerId, setGdmsSipServerId] = useState(
    agent?.gdms_sip_server_id != null ? String(agent.gdms_sip_server_id) : '',
  );
  const [commioRouteId, setCommioRouteId] = useState(
    agent?.commio_route_id != null ? String(agent.commio_route_id) : '',
  );
  const [suspendedDestination, setSuspendedDestination] = useState(agent?.suspended_destination || '');

  const hasStoredSecret = !!agent?.has_freepbx_client_secret;
  const isFreepbx       = pbxType === 'freepbx';

  // Snapshot the initial values so we can distinguish "user didn't touch this
  // field" from "user cleared this field." Without this, saving with only
  // gdms_sip_server_id changed would clobber freepbx_client_id / baseUrl
  // (both start empty when the API list-view omits them). Confirmed as a
  // real prod incident 2026-07-17 — freepbx_client_id got NULL'd on a
  // gdms-only save.
  const initial = {
    name: agent?.name || agent?.label || '',
    description: agent?.description || '',
    pbxType: agent?.pbx_type || 'freepbx',
    clientId: agent?.freepbx_client_id || '',
    baseUrl: agent?.freepbx_base_url || DEFAULT_BASE_URL,
    gdmsSipServerId: agent?.gdms_sip_server_id != null ? String(agent.gdms_sip_server_id) : '',
    commioRouteId: agent?.commio_route_id != null ? String(agent.commio_route_id) : '',
    suspendedDestination: agent?.suspended_destination || '',
  };

  // GDMS SIP servers registered for this org. Populated on modal open —
  // dropdown is empty (and shows a hint) if GDMS isn't configured / down.
  const { data: sipServersResp } = useQuery({
    queryKey: ['gdms-sip-servers'],
    queryFn: () => api.get('/settings/gdms/sip-servers'),
    retry: false,
  });
  const sipServers = Array.isArray(sipServersResp?.servers) ? sipServersResp.servers : [];

  // Commio inbound static-route profiles for this org. Feeds the "Commio
  // route" dropdown — the profile that delivers DIDs to this PBX host.
  const { data: routeProfilesResp } = useQuery({
    queryKey: ['commio-routing-profiles'],
    queryFn: () => api.get('/settings/commio/routing-profiles'),
    retry: false,
  });
  const routeProfiles = Array.isArray(routeProfilesResp?.profiles) ? routeProfilesResp.profiles : [];

  // FreePBX announcements on this host — live read through the agent, feeds
  // the suspended-routing destination picker so nobody has to hand-type
  // "app-announcement-N,s,1".
  const { data: annResp } = useQuery({
    queryKey: ['agent-announcements', agentDisplayIdForQuery(agent)],
    queryFn: () => api.get(`/agents/${agentDisplayIdForQuery(agent)}/proxy/announcements`),
    retry: false,
    enabled: !!agentDisplayIdForQuery(agent),
  });
  const announcements = Array.isArray(annResp) ? annResp : [];

  function handleSubmit(e) {
    e.preventDefault();
    // Only include a field in the PATCH body when the user changed it. This
    // prevents a partial edit (e.g. "just change the GDMS SIP server") from
    // sending stale-empty values for other fields and clobbering them.
    const body = {};
    if (name.trim() !== initial.name)                body.name = name.trim();
    if (description.trim() !== initial.description)  body.description = description.trim();
    if (pbxType !== initial.pbxType)                 body.pbx_type = pbxType;
    if (gdmsSipServerId !== initial.gdmsSipServerId) {
      body.gdms_sip_server_id = gdmsSipServerId === '' ? null : Number(gdmsSipServerId);
    }
    if (commioRouteId !== initial.commioRouteId) {
      body.commio_route_id = commioRouteId === '' ? null : Number(commioRouteId);
    }
    if (suspendedDestination.trim() !== initial.suspendedDestination) {
      body.suspended_destination = suspendedDestination.trim() || null;
    }
    if (isFreepbx) {
      if (clientId.trim() !== initial.clientId)      body.freepbx_client_id = clientId.trim();
      if (baseUrl.trim() !== initial.baseUrl)        body.freepbx_base_url  = baseUrl.trim();
      // Secret follows the "blank preserves" convention — only send when the
      // user actually typed a new value.
      if (clientSecret.length > 0)                   body.freepbx_client_secret = clientSecret;
    }
    if (Object.keys(body).length === 0) {
      // Nothing changed — close the modal without a network round-trip.
      onSave(body); // parent decides whether to no-op or refetch
      return;
    }
    onSave(body);
  }

  function handleClearSecret() {
    if (!confirm('Clear the stored FreePBX client_secret? The agent will lose FreePBX API access until a new secret is saved.')) return;
    onSave({ freepbx_client_secret: '' });
  }

  const agentDisplayId = agent?.id || agent?.agent_id;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-gray-900/50 p-4">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-lg rounded-lg bg-white p-6 shadow-lg"
      >
        <h2 className="text-lg font-semibold text-gray-900">Edit PBX Host</h2>
        <p className="mt-1 text-xs text-gray-400 font-mono">{agentDisplayId}</p>

        <div className="mt-4 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-700">Display name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              minLength={2}
              maxLength={80}
              className="mt-1 block w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700">Notes</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={500}
              rows={2}
              className="mt-1 block w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          <PbxTypeSelect value={pbxType} onChange={setPbxType} />

          <div>
            <label className="block text-xs font-medium text-gray-700">
              GDMS SIP server
            </label>
            <select
              value={gdmsSipServerId}
              onChange={(e) => setGdmsSipServerId(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">— none — (device provisioning will fail until set)</option>
              {sipServers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.serverName || `#${s.id}`}
                  {s.sipServer ? ` — ${s.sipServer}` : ''}
                  {typeof s.accountNumber === 'number' ? ` (${s.accountNumber} accts)` : ''}
                </option>
              ))}
            </select>
            <p className="mt-1 text-[11px] text-gray-500">
              Which GDMS SIP server record this PBX host maps to. Device
              provisioning binds SIP accounts to whichever server is assigned
              here. GDMS may hold multiple entries per PBX (different network
              paths) — pick the one devices will reach.
              {sipServers.length === 0 && (
                <span className="text-amber-700">
                  {' '}No GDMS SIP servers loaded — check Settings → Integrations → GDMS.
                </span>
              )}
            </p>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700">
              Commio route
            </label>
            <select
              value={commioRouteId}
              onChange={(e) => setCommioRouteId(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">— none — (carrier routing will fail until set)</option>
              {commioRouteId
                && !routeProfiles.some((p) => String(p.id) === String(commioRouteId)) && (
                <option value={commioRouteId}>
                  #{commioRouteId} (current — not in profile list)
                </option>
              )}
              {routeProfiles.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name || p.description || `#${p.id}`}
                </option>
              ))}
            </select>
            <p className="mt-1 text-[11px] text-gray-500">
              Commio inbound static-route profile that delivers this host's
              DIDs to it. Provisioning points every assigned DID at this
              profile — so a DID moving hosts follows the host's route.
              {routeProfiles.length === 0 && (
                <span className="text-amber-700">
                  {' '}No Commio route profiles loaded — check Settings → Integrations → Commio.
                </span>
              )}
            </p>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700">
              Suspended-routing announcement
            </label>
            <select
              value={suspendedDestination}
              onChange={(e) => setSuspendedDestination(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">— none — (suspend will fail until set)</option>
              {/* Preserve a stored value that isn't in the current catalog
                  (announcement deleted, or a hand-set custom destination). */}
              {suspendedDestination
                && !announcements.some((a) => a.destination === suspendedDestination) && (
                <option value={suspendedDestination}>
                  {suspendedDestination} (current — not in announcement list)
                </option>
              )}
              {announcements.map((a) => (
                <option key={a.id} value={a.destination}>
                  {a.description || `Announcement #${a.id}`}
                </option>
              ))}
            </select>
            <p className="mt-1 text-[11px] text-gray-500">
              Inbound calls to suspended subscribers' numbers play this
              FreePBX announcement (Applications → Announcements on the PBX).
              Suspend fails until one is chosen.
              {announcements.length === 0 && (
                <span className="text-amber-700">
                  {' '}No announcements loaded — the agent may need an update
                  (requires verso-agent with /announcements support).
                </span>
              )}
            </p>
          </div>

          {isFreepbx && (
            <fieldset className="rounded-md border border-gray-200 p-3">
              <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                {PBX_TYPES.freepbx.label} API
              </legend>
              <p className="text-[11px] text-gray-500 mb-2">
                Cloud stores these; the agent pulls them from{' '}
                <span className="font-mono">/agents/&hellip;/config</span> at
                startup and periodically. Rotate here without touching the
                agent's disk.
              </p>

              <div>
                <label className="block text-xs font-medium text-gray-700">Client ID</label>
                <input
                  type="text"
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  placeholder="128-char client_id from FreePBX API Application"
                  className="mt-1 block w-full rounded-md border border-gray-300 px-2 py-1.5 font-mono text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div className="mt-3">
                <label className="flex items-center justify-between text-xs font-medium text-gray-700">
                  <span>
                    Client Secret
                    {hasStoredSecret && (
                      <span className="ml-2 rounded bg-green-50 px-1.5 py-0.5 text-[10px] font-medium text-green-700">
                        stored
                      </span>
                    )}
                  </span>
                  <button
                    type="button"
                    onClick={() => setShowSecret((s) => !s)}
                    className="text-xs font-normal text-blue-600 hover:underline"
                  >
                    {showSecret ? 'Hide' : 'Show'}
                  </button>
                </label>
                <input
                  type={showSecret ? 'text' : 'password'}
                  value={clientSecret}
                  onChange={(e) => setClientSecret(e.target.value)}
                  placeholder={hasStoredSecret ? '(unchanged)' : '64-char client_secret'}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-2 py-1.5 font-mono text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  autoComplete="new-password"
                />
                <p className="mt-1 text-[11px] text-gray-500">
                  FreePBX only shows the secret once at creation. Paste it
                  here and the cloud will encrypt + persist it.
                </p>
              </div>

              <div className="mt-3">
                <label className="block text-xs font-medium text-gray-700">Base URL</label>
                <input
                  type="text"
                  value={baseUrl}
                  onChange={(e) => setBaseUrl(e.target.value)}
                  placeholder={DEFAULT_BASE_URL}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-2 py-1.5 font-mono text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <p className="mt-1 text-[11px] text-gray-500">
                  Usually{' '}
                  <span className="font-mono">{DEFAULT_BASE_URL}</span> —
                  agent and FreePBX share the same host.
                </p>
              </div>
            </fieldset>
          )}
        </div>

        {error && (
          <p className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            {error}
          </p>
        )}

        <div className="mt-6 flex items-center justify-between">
          <div>
            {isFreepbx && hasStoredSecret && (
              <button
                type="button"
                onClick={handleClearSecret}
                disabled={isPending}
                className="rounded-md border border-red-300 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                title="Delete the stored FreePBX client_secret. Agent will lose FreePBX access."
              >
                Clear secret
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={isPending}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending || name.trim().length < 2}
              className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
